import React from 'react';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '../utils/useDocumentMeta';

/**
 * Privacy Policy — required by:
 *   • LinkedIn / Meta / Google Ads (won't approve campaigns without one)
 *   • Stripe (payment processor TOS requirement)
 *   • GDPR (EU/UK), CCPA (California), PIPEDA (Canada), LGPD (Brazil)
 *   • CookieHub consent banner (links here for category details)
 *
 * Plain-language draft tuned to Ascend's actual data flows. NOT legal advice;
 * recommend a final review by qualified counsel before scaling marketing
 * spend or expanding to new jurisdictions.
 */
const PrivacyPolicy = () => {
  const lastUpdated = 'April 24, 2026';
  useDocumentMeta({
    title: 'Privacy Policy — Ascend',
    description: 'How Ascend collects, uses, and protects your personal data. GDPR, CCPA, and LGPD compliant.',
    path: '/privacy',
  });
  return (
    <>
      <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Link to="/" className="text-sm text-[#002fa7] hover:underline mb-8 inline-block">← Back to Ascend</Link>

          <h1 className="font-satoshi text-4xl lg:text-5xl font-bold tracking-tight text-[#0f1115] mb-3">
            Privacy Policy
          </h1>
          <p className="text-sm text-[#6b7280] mb-10">Last updated: {lastUpdated}</p>

          <div className="prose prose-lg max-w-none text-[#374151] leading-relaxed">

            <p className="text-lg">
              Ascend (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;Ascend&rdquo;) operates ascendforyou.net (the &ldquo;Service&rdquo;).
              This policy explains what information we collect, how we use it, and the rights you have over it.
              If you have questions, email <a href="mailto:hello@ascendforyou.net" className="text-[#002fa7] underline">hello@ascendforyou.net</a>.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">1. Information we collect</h2>

            <h3 className="font-satoshi font-bold text-lg text-[#0f1115] mt-6 mb-2">a. Information you give us</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account data:</strong> name, email address, password (hashed), and (optional) profile photo when you sign up or sign in with Google.</li>
              <li><strong>Resume content:</strong> resumes you paste, upload (PDF/DOCX), edit, or generate, including any personal details they contain (work history, education, contact information).</li>
              <li><strong>Job-targeting data:</strong> job descriptions, role titles, and company names you use Ascend to tailor against.</li>
              <li><strong>Payment information:</strong> processed by Stripe. We never see or store your card details — only the metadata Stripe returns (last 4 digits, brand, expiry, billing country).</li>
              <li><strong>Lead form data:</strong> email submitted to the Free ATS Check tool, used to send you your score and (with consent) follow-up career tips.</li>
              <li><strong>Communications:</strong> any messages you send to support or via our forms.</li>
            </ul>

            <h3 className="font-satoshi font-bold text-lg text-[#0f1115] mt-6 mb-2">b. Information we collect automatically</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Usage data:</strong> pages visited, features used, clicks, session timestamps, device and browser type, approximate location (city/country level, derived from IP).</li>
              <li><strong>Cookies and similar technologies:</strong> see <a href="#cookies" className="text-[#002fa7] underline">Section 5</a>.</li>
              <li><strong>Server logs:</strong> IP address, request headers, error reports — kept for security, abuse prevention, and debugging.</li>
            </ul>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">2. How we use your information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide, maintain, and improve the Service (including AI resume scoring, rewriting, and matching).</li>
              <li>Process payments and manage subscriptions through Stripe.</li>
              <li>Send transactional emails (sign-up confirmation, password reset, payment receipts, magic links).</li>
              <li>Send marketing emails about new features, tips, and offers — only with your consent, and with one-click unsubscribe in every message.</li>
              <li>Detect, investigate, and prevent fraud, abuse, and security incidents.</li>
              <li>Comply with legal obligations and respond to lawful requests.</li>
              <li>Conduct analytics to understand usage patterns and improve product decisions.</li>
            </ul>

            <p className="mt-4">
              <strong>We do not sell your personal information.</strong> We do not use your resume content to train third-party AI models without your explicit instruction.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">3. AI processing of your resume</h2>
            <p>
              When you use AI features (rewriting, ATS scoring, cover letter generation, job matching), your resume text and the relevant job description are sent to our AI processing partners
              (currently Anthropic Claude, OpenAI, and Google Gemini) under enterprise privacy terms that prohibit them from using your data to train their models.
              We retain only the minimum data needed to display your results and history. You can delete your resumes at any time from your dashboard, which permanently removes them from our systems within 30 days.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">4. Who we share information with</h2>
            <p>We share data only with the service providers required to operate Ascend, each bound by data processing agreements:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Stripe</strong> — payment processing</li>
              <li><strong>Resend</strong> — transactional and marketing email delivery</li>
              <li><strong>Anthropic, OpenAI, Google (Gemini)</strong> — AI text generation and scoring</li>
              <li><strong>MongoDB Atlas</strong> — encrypted database hosting</li>
              <li><strong>Google Analytics 4</strong> — anonymized usage analytics</li>
              <li><strong>LinkedIn Insight Tag, Meta Pixel</strong> — advertising performance and audience building (only fired if you accept marketing cookies)</li>
              <li><strong>CookieHub</strong> — cookie consent management</li>
              <li><strong>RapidAPI / JSearch</strong> — job listings on the Job Board</li>
            </ul>
            <p className="mt-4">
              We may also disclose information to comply with valid legal process, protect our rights, or in connection with a merger or acquisition (you would be notified of any change in data controller).
            </p>

            <h2 id="cookies" className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">5. Cookies and tracking</h2>
            <p>
              We use cookies and similar technologies in three categories:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Strictly necessary</strong> (always on): authentication, session, security, payment flow.</li>
              <li><strong>Analytics</strong> (consent-based): Google Analytics 4, used in anonymized form to understand product usage.</li>
              <li><strong>Marketing</strong> (consent-based): LinkedIn Insight Tag and Meta Pixel, used to measure ad performance and show you relevant ads on those platforms after you visit Ascend.</li>
            </ul>
            <p className="mt-4">
              You can change your preferences any time using the cookie settings link in the consent banner footer, or by clearing your site data in your browser.
              EU/UK and California users see an opt-in banner by default and no analytics or marketing cookies fire until you accept.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">6. Data retention</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account data:</strong> kept while your account is active; deleted within 30 days of account deletion request.</li>
              <li><strong>Resume and AI output data:</strong> kept until you delete it; permanently removed within 30 days of deletion.</li>
              <li><strong>Payment records:</strong> kept for 7 years to satisfy tax and accounting laws.</li>
              <li><strong>Server logs:</strong> kept up to 90 days for security and debugging.</li>
              <li><strong>Lead emails (Free ATS Check):</strong> kept until you unsubscribe or request deletion.</li>
            </ul>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">7. Your rights</h2>
            <p>Depending on where you live, you may have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Access the personal data we hold about you</li>
              <li>Correct inaccurate data</li>
              <li>Delete your data (&ldquo;right to be forgotten&rdquo;)</li>
              <li>Export your data in a portable format</li>
              <li>Object to or restrict certain processing</li>
              <li>Withdraw consent at any time</li>
              <li>Lodge a complaint with your local data protection authority</li>
            </ul>
            <p className="mt-4">
              To exercise any of these rights, email <a href="mailto:hello@ascendforyou.net" className="text-[#002fa7] underline">hello@ascendforyou.net</a> from the email address on your account.
              We&rsquo;ll respond within 30 days.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">8. Security</h2>
            <p>
              We use TLS encryption in transit, encryption at rest for sensitive fields, bcrypt password hashing, OAuth-based authentication, role-based access controls, and Stripe&rsquo;s PCI-DSS-Level-1
              infrastructure for all payments. No system is perfectly secure, but we apply industry-standard practices and review our posture regularly.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">9. Children</h2>
            <p>
              Ascend is not directed to children under 16. We do not knowingly collect data from anyone under 16. If you believe a child has provided us data, contact us and we&rsquo;ll delete it.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">10. International transfers</h2>
            <p>
              Ascend is operated from the United States. If you access the Service from outside the US, your data may be transferred to and processed in the US.
              For EU/UK users, we rely on Standard Contractual Clauses with our sub-processors to ensure equivalent protection.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">11. Changes to this policy</h2>
            <p>
              We may update this policy occasionally. Material changes will be announced by email or in-app banner at least 14 days before they take effect.
              The &ldquo;Last updated&rdquo; date at the top reflects the most recent revision.
            </p>

            <h2 className="font-satoshi font-bold text-2xl text-[#0f1115] mt-12 mb-4">12. Contact us</h2>
            <p>
              For any privacy question, complaint, or rights request:
              <br />
              📧 <a href="mailto:hello@ascendforyou.net" className="text-[#002fa7] underline">hello@ascendforyou.net</a>
              <br />
              🌐 <a href="https://ascendforyou.net" className="text-[#002fa7] underline">ascendforyou.net</a>
            </p>

          </div>

          <div className="mt-16 pt-8 border-t border-[#e5e7eb] flex flex-wrap gap-4 text-sm">
            <Link to="/" className="text-[#4b5563] hover:text-[#002fa7]">Home</Link>
            <Link to="/terms" className="text-[#4b5563] hover:text-[#002fa7]">Terms of Service</Link>
            <Link to="/pricing" className="text-[#4b5563] hover:text-[#002fa7]">Pricing</Link>
            <a href="mailto:hello@ascendforyou.net" className="text-[#4b5563] hover:text-[#002fa7]">Contact</a>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
